/**
 * What search engines and link previews read about each Arche Store page: its
 * title, description, canonical address, and structured data (schema.org
 * JSON-LD). One source for the app (which sets them as the page changes) and
 * the static snapshots, so the two cannot describe a page differently.
 *
 * An app page is a `WebApplication` (a kind of `SoftwareApplication`) that
 * runs in the browser, with its screenshots and its price as an `Offer`. No ratings are claimed:
 * the store has none (Google gives a software app a star result only with
 * real ratings, and we never invent them).
 */
import { absolute, breadcrumbs, clip, type PageSeo } from '../seo';
import { appCount, priceLabel } from './format';
import {
  STORE_APPS_PATH,
  STORE_CATEGORIES_PATH,
  STORE_HOME_PATH,
  STORE_TOP_PATH,
  storeAppPagePath,
  storeCategoryPath,
  storeStoryPath,
} from './routes';
import type {
  StoreAppPageData,
  StoreAppsPageData,
  StoreCard,
  StoreCategoryRef,
  StoreCollectionData,
  StoreHomeData,
  StoreTopData,
} from './types';

const SITE = 'Arche Store';

/**
 * The schema.org `applicationCategory` for each of our categories, from the
 * values Google documents for software apps; the rest are free text.
 */
const SCHEMA_CATEGORY: Readonly<Record<string, string>> = {
  'ai-assistants': 'UtilitiesApplication',
  productivity: 'BusinessApplication',
  business: 'BusinessApplication',
  finance: 'FinanceApplication',
  communication: 'CommunicationApplication',
  social: 'SocialNetworkingApplication',
  'graphics-design': 'DesignApplication',
  'photo-video': 'MultimediaApplication',
  'music-audio': 'MultimediaApplication',
  education: 'EducationalApplication',
  'developer-tools': 'DeveloperApplication',
  utilities: 'UtilitiesApplication',
  entertainment: 'EntertainmentApplication',
  lifestyle: 'LifestyleApplication',
  'health-fitness': 'HealthApplication',
  travel: 'TravelApplication',
  'food-drink': 'LifestyleApplication',
  shopping: 'ShoppingApplication',
  news: 'ReferenceApplication',
  reference: 'ReferenceApplication',
  sports: 'SportsApplication',
};

function itemList(origin: string, cards: StoreCard[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: cards.map((card, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: card.title,
      url: absolute(origin, card.pagePath),
    })),
  };
}

export function storeTodaySeo(data: StoreHomeData, origin: string): PageSeo {
  const shown = [
    ...data.today.flatMap((c) =>
      c.kind === 'daily' ? [c.pick.card] : c.collection.items.map((i) => i.card),
    ),
    ...data.topApps,
  ].slice(0, 20);
  return {
    title: `${SITE} — apps and games for your browser | Fias`,
    description:
      'Discover apps and games that run right in your browser: today’s picks, the top apps, and every category on the Fias Arche Store.',
    canonicalPath: STORE_HOME_PATH,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Arche Store',
        url: absolute(origin, STORE_HOME_PATH),
      },
      ...(shown.length > 0 ? [itemList(origin, shown)] : []),
    ],
  };
}

export function storeAppsSeo(data: StoreHomeData, origin: string): PageSeo {
  const top = data.topApps.slice(0, 20);
  return {
    title: `Apps — productivity, AI, business and more | ${SITE}`,
    description:
      'Apps for work, money, learning, design and more, that open in your browser. See what’s new, the top apps, and every category.',
    canonicalPath: STORE_APPS_PATH,
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        { name: 'Apps', path: STORE_APPS_PATH },
      ]),
      ...(top.length > 0 ? [itemList(origin, top)] : []),
    ],
  };
}

export function storeCategoriesSeo(categories: StoreCategoryRef[], origin: string): PageSeo {
  const names = categories
    .filter((c) => (c.count ?? 0) > 0)
    .slice(0, 6)
    .map((c) => c.label)
    .join(', ');
  return {
    title: `App categories | ${SITE}`,
    description: clip(
      `Browse apps by category on the Fias Arche Store${names ? `: ${names} and more` : ''}.`,
    ),
    canonicalPath: STORE_CATEGORIES_PATH,
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        { name: 'Categories', path: STORE_CATEGORIES_PATH },
      ]),
    ],
  };
}

export function storeCategorySeo(
  data: StoreAppsPageData & { category: StoreCategoryRef },
  origin: string,
): PageSeo {
  const { category, cards, total } = data;
  const examples = cards
    .slice(0, 3)
    .map((c) => c.title)
    .join(', ');
  return {
    title: `${category.label} apps | ${SITE}`,
    description: clip(
      `${appCount(total)} in ${category.label} on the Fias Arche Store${
        examples ? `, including ${examples}` : ''
      }. They open in your browser.`,
    ),
    canonicalPath: storeCategoryPath(category.id),
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        { name: 'Apps', path: STORE_APPS_PATH },
        { name: category.label, path: storeCategoryPath(category.id) },
      ]),
      ...(cards.length > 0 ? [itemList(origin, cards.slice(0, 50))] : []),
    ],
  };
}

export function storeTopSeo(data: StoreTopData, origin: string): PageSeo {
  return {
    title: `Top apps | ${SITE}`,
    description:
      'The most opened apps on the Fias Arche Store, in order — the apps people come back to.',
    canonicalPath: STORE_TOP_PATH,
    jsonLd: [
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        { name: 'Top apps', path: STORE_TOP_PATH },
      ]),
      ...(data.cards.length > 0 ? [itemList(origin, data.cards.slice(0, 100))] : []),
    ],
  };
}

export function storeStorySeo(story: StoreCollectionData, origin: string): PageSeo {
  const path = storeStoryPath(story.slug);
  const cards = story.items.map((i) => i.card);
  return {
    title: `${story.title} | ${SITE}`,
    description: clip(story.tagline ?? story.body ?? `${appCount(cards.length)} picked by Fias.`),
    canonicalPath: path,
    ...(story.heroUrl ? { image: story.heroUrl } : {}),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: story.title,
        ...(story.tagline ? { description: story.tagline } : {}),
        url: absolute(origin, path),
        dateModified: story.updatedAt,
        publisher: { '@type': 'Organization', name: 'Fias' },
        ...(story.heroUrl ? { image: story.heroUrl } : {}),
      },
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        { name: story.title, path },
      ]),
      ...(cards.length > 0 ? [itemList(origin, cards)] : []),
    ],
  };
}

export function storeAppSeo(
  data: StoreAppPageData,
  origin: string,
  categoryLabels: Readonly<Record<string, string>> = {},
): PageSeo {
  const { card } = data;
  const path = storeAppPagePath(card.slug);
  const icon = card.iconUrlLarge ?? card.iconUrl ?? undefined;
  // A link preview shows the first landscape screenshot where there is one.
  const wide = data.screenshots.find((s) => s.width > s.height);
  const image = wide?.url ?? icon;
  const primary = card.categories[0];
  const tagline = card.subtitle ?? (primary ? categoryLabels[primary] : null);
  const fixedPrice =
    data.pricing?.model === 'fixed' && data.pricing.priceCents ? data.pricing.priceCents / 100 : 0;
  return {
    title: tagline ? `${card.title} — ${tagline} | ${SITE}` : `${card.title} | ${SITE}`,
    description: clip(card.description || data.about),
    canonicalPath: path,
    image,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: card.title,
        description: clip(data.about || card.description, 500),
        url: absolute(origin, path),
        ...(icon ? { image: icon } : {}),
        ...(data.screenshots.length > 0 ? { screenshot: data.screenshots.map((s) => s.url) } : {}),
        applicationCategory: (primary && SCHEMA_CATEGORY[primary]) || 'WebApplication',
        ...(primary && categoryLabels[primary]
          ? { applicationSubCategory: categoryLabels[primary] }
          : {}),
        operatingSystem: 'Web browser',
        browserRequirements: 'Requires a modern web browser',
        offers: {
          '@type': 'Offer',
          price: fixedPrice.toFixed(2),
          priceCurrency: 'USD',
          ...(priceLabel(data.pricing) === 'Pay as you go'
            ? { description: 'Free to open; AI use is billed as you go' }
            : {}),
        },
        ...(data.publisher
          ? {
              publisher: {
                '@type': data.publisher.kind === 'person' ? 'Person' : 'Organization',
                name: data.publisher.name,
              },
            }
          : {}),
        ...(data.version ? { softwareVersion: data.version } : {}),
        ...(data.updatedAt ? { dateModified: data.updatedAt } : {}),
        ...(data.languages.length > 0 ? { inLanguage: data.languages } : {}),
        ...(card.kind === 'listing'
          ? { isPartOf: { '@type': 'WebApplication', name: card.archeName } }
          : {}),
      },
      breadcrumbs(origin, [
        { name: 'Arche Store', path: STORE_HOME_PATH },
        ...(primary
          ? [{ name: categoryLabels[primary] ?? primary, path: storeCategoryPath(primary) }]
          : [{ name: 'Apps', path: STORE_APPS_PATH }]),
        { name: card.title, path },
      ]),
    ],
  };
}
